import PropTypes from 'prop-types';
import { Radio } from './Radio';

/**
 * RadioGroup Component
 *
 * Group of radio buttons for single-selection input.
 * Commonly used for Yes/No, SI/NO questions.
 *
 * @param {Object} props
 * @param {string} props.name - Name for the radio group (all options share this name)
 * @param {Array<{value: string, label: string}>} props.options - Array of radio options
 * @param {string} props.value - Currently selected value
 * @param {Function} props.onChange - Callback when selection changes (receives selected value)
 * @param {boolean} props.disabled - Whether all radios are disabled
 * @param {string} props.direction - Layout direction: 'horizontal' or 'vertical'
 *
 * @example
 * <RadioGroup
 *   name="drivers_license"
 *   options={[
 *     { value: 'yes', label: 'Sí' },
 *     { value: 'no', label: 'No' }
 *   ]}
 *   value={driversLicense}
 *   onChange={setDriversLicense}
 * />
 */
export function RadioGroup({
  name,
  options,
  value,
  onChange,
  disabled = false,
  direction = 'horizontal'
}) {
  const containerClass = direction === 'horizontal'
    ? 'flex gap-6'
    : 'space-y-2';

  return (
    <div className={containerClass}>
      {options.map((option) => (
        <Radio
          key={option.value}
          name={name}
          label={option.label}
          value={option.value}
          checked={value === option.value}
          onChange={onChange}
          disabled={disabled}
        />
      ))}
    </div>
  );
}

RadioGroup.propTypes = {
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  direction: PropTypes.oneOf(['horizontal', 'vertical']),
};
